import {MetricType} from "../../pages/MetricsTest";
import {MetricFunction} from "./widgets/MetricSelector";
import {ChartType} from "../Charts/MetoroChart";
import {MetricSpecifier, MetoroQLQuerySpecifier} from "../../clients/metoro/metrics";
import {Formula} from "../../types/formula";
import {ColumnConfig} from "../ui/datatable/datatable";
import {Transform} from "./widgets/TransformSelector";

type WidgetType = 'MetricChart'| 'Group' | 'Markdown' | "Stat" | "Trace" | "Log" | "Gauge"

// This is relative to the parent
interface WidgetPosition {
    x: number | undefined,
    y: number | undefined,
    w: number | undefined,
    h: number | undefined,
}

interface Widget {
    widgetType: WidgetType
    position: WidgetPosition | undefined
}

interface Variable {
    name: string
    key: string
    defaultValue: string
    defaultType: MetricType
    defaultMetricName?: string
    isOverrideable: boolean
}

// This defines all we need to rehydrate a group widget
interface GroupWidget extends Widget {
    title?: string,
    children: Widget[],
    variables?: Variable[]
}

// This defines all we need to rehydrate a metric chart widget
interface OldMetricChartWidget extends Widget {
    metricName: string;
    filters?: Map<string, string[]>;
    excludeFilters?: Map<string, string[]>;
    splits?: string[];
    aggregation: string;
    title?: string;
    type: ChartType;
    metricType: MetricType; // could be metric or trace.
    functions: MetricFunction[];
}

interface MultiMetricChartWidget extends Widget {
    title?: string
    type: ChartType;
    metricSpecifiers: MetricSpecifier[]
    formulas?: Formula[]
    metoroQLQueries?: MetoroQLQuerySpecifier[]
    displaySettings?: MetricChartDisplaySettings
}

interface MetricChartDisplaySettings {
    thresholds?: ChartThreshold
}

interface ChartThreshold {
   steps: ChartThresholdStep[]
}

interface ChartThresholdStep {
    value: number
    color: string
    label?: string
}

interface MarkdownWidget extends Widget {
    content: string
}

interface OldStatWidget extends Widget {
    title?: string
    metricSpecifier: MetricSpecifier,
    reduceOption: ReduceOption
    mappings?: StatWidgetMapping[]
    precision?: number  // Number of decimal points to display
    fontSize?: string  // Font size for the displayed value (e.g. "text-xl", "text-2xl", "text-3xl", etc.)
}

interface StatWidget extends Widget {
    title?: string
    metricSpecifiers: MetricSpecifier[]
    formulas?: Formula[]
    metoroQLQueries?: MetoroQLQuerySpecifier[]
    reduceOption: ReduceOption
    mappings?: StatWidgetMapping[]
    precision?: number  // Number of decimal points to display
    fontSize?: string  // Font size for the displayed value (e.g. "text-xl", "text-2xl", "text-3xl", etc.)
    columnConfigs?: ColumnConfig[]
    unitOverride?: string  // Optional unit override for the displayed value
}

type ReduceOption = "avg" | "sum" | "max" | "min" | "lastNotNull" | "last"
export const ReduceOptions = ["avg", "sum", "max", "min", "lastNotNull", "last"]

interface StatWidgetMapping {
    operator: ">" | "<" | "==" | ">=" | "<=" | "range"
    value: number
    from?: number
    to?: number
    color: string
    type: "background" | "text"
    textMapping?: string
}


interface TraceWidget extends Widget {
    title?: string
    filters?: Map<string, string[]>
    excludeFilters?: Map<string, string[]>
    regexes?: string[],
    excludeRegexes?: string[],
    visualization?: TraceWidgetVisualization
}

interface TraceWidgetVisualization {
    hideSearchBar?: boolean
    visibleColumns?: string[]
}

export enum TraceWidgetColumnName {
    Time = "time",
    TraceId = "traceId",
    Client = "client.service.name",
    Service = "server.service.name",
    Status = "status",
    Method = "method",
    Endpoint = "endpoint",
    Latency = "latency"
}

export const TraceWidgetColumns = [
    "time", "traceId",
    "client.service.name", "server.service.name",
    "status",  "method",
    "endpoint", "latency"
];


interface LogWidget extends Widget {
    title?: string
    filters?: Map<string, string[]>
    excludeFilters?: Map<string, string[]>
    regexes?: string[],
    excludeRegexes?: string[],
    transforms?: Transform[]
    metoroQLQuery?: string,
    visualization?: LogWidgetVisualization
}

interface LogWidgetVisualization {
    hideSearchBar?: boolean
    hideServiceColumn?: boolean
    hideTimeColumn?: boolean
    customColumns?: string[]
}

interface GaugeWidget extends Widget {
    title?: string
    label?: string
    min?: number
    max?: number
    metricSpecifiers: MetricSpecifier[]
    formulas?: Formula[]
    metoroQLQueries?: MetoroQLQuerySpecifier[]
    reduceOption: ReduceOption
    precision?: number  // Number of decimal points to display
    showAbsoluteValue?: boolean  // When true, display raw value instead of percentage
    unitOverride?: string  // Optional unit override for the displayed value
}

export type {Widget, OldStatWidget, GroupWidget, OldMetricChartWidget, MultiMetricChartWidget, MetricChartDisplaySettings, WidgetType, WidgetPosition, MarkdownWidget, Variable, StatWidget, ReduceOption, StatWidgetMapping, LogWidget, TraceWidget, TraceWidgetVisualization, GaugeWidget}
